import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X, AlertTriangle, Package, LayoutGrid, GripVertical, Mail, Tag, FolderInput, CheckSquare, Square, ShoppingCart } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import HomeSectionsManager from "../components/HomeSectionsManager";
import CategoriesManager from "../components/CategoriesManager";
import SectionsManager from "../components/SectionsManager";
import RestockWaitlist from "../components/RestockWaitlist";
import LiveCartsWidget from "../components/LiveCartsWidget";
import AdminOrders from "../components/AdminOrders";
import LowStockBanner from "../components/LowStockBanner";

const empty = {
  title: "", description: "", price: 0, category: "digital",
  image_url: "", gallery: [], is_digital: true, download_url: "", badge: "", stock: 100,
  variants: [], colors: [], meta_title: "", meta_description: "", sections: [],
};

const MAX_GALLERY = 9; // 1 main + 9 gallery = 10 photos total

// Default size sets
const ADULT_REGULAR = ["XS", "S", "M", "L", "XL"];
const ADULT_PLUS = ["1X", "2X", "3X", "4X"];
const YOUTH = ["Youth S", "Youth M", "Youth L", "Youth XL"];
const ALL_SIZES = [...ADULT_REGULAR, ...ADULT_PLUS, ...YOUTH];

// Auto-resize huge phone photos client-side so uploads are fast and never hit the backend cap.
// Long-edge max 2400px, JPEG q=0.85. Returns the original File untouched if it's already small.
async function maybeResizeImage(file) {
  if (!file || !file.type.startsWith("image/")) return file;
  if (file.size < 2 * 1024 * 1024) return file; // <2MB — don't bother
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const MAX = 2400;
    const longEdge = Math.max(bitmap.width, bitmap.height);
    const scale = longEdge > MAX ? MAX / longEdge : 1;
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob) return file;
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}


export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [cartCounts, setCartCounts] = useState({});
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCategory, setBulkCategory] = useState("");
  const [reorderMode, setReorderMode] = useState(false);
  const reorderDragId = useRef(null);
  const dragIndex = useRef(null);

  const fetchProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  const fetchCategories = () => api.get("/admin/categories").then(({ data }) => setCategories(data));
  const fetchSections = () => api.get("/admin/sections").then(({ data }) => setSections(data));
  const fetchCartCounts = () => api.get("/admin/live-carts/counts").then(({ data }) => setCartCounts(data || {})).catch(() => setCartCounts({}));

  useEffect(() => { if (user && user.role === "admin") { fetchProducts(); fetchCategories(); fetchSections(); fetchCartCounts(); } }, [user]);

  // Reorder mode only makes sense within a specific category — auto-exit otherwise
  useEffect(() => {
    if (reorderMode && (categoryFilter === "all" || categoryFilter === "__orphans__")) {
      setReorderMode(false);
    }
  }, [categoryFilter, reorderMode]);

  if (authLoading) return <div className="text-center py-24 text-[#8a8076]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin" }} replace/>;
  if (user.role !== "admin") return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="font-script text-3xl text-[#e8478e]">No Entry</div>
      <h1 className="font-display text-4xl font-black text-[#1c1815]">Admin Only</h1>
      <p className="text-[#4a4239] mt-2">This area requires admin access.</p>
    </div>
  );

  const filteredProducts = (() => {
    if (categoryFilter === "all") return products;
    if (categoryFilter === "__orphans__") {
      const validSlugs = new Set(categories.map((c) => c.slug));
      return products.filter((p) => !validSlugs.has(p.category));
    }
    return products.filter((p) => p.category === categoryFilter);
  })();

  // Selected category info for the "New Product in [Category]" pre-fill
  const selectedCat = categories.find((c) => c.slug === categoryFilter);

  const startNew = () => {
    // Prefill category from the active filter (or first available category)
    const presetSlug = selectedCat ? selectedCat.slug : (categories[0]?.slug || "");
    const presetIsDigital = presetSlug === "digital";
    setForm({ ...empty, category: presetSlug, is_digital: presetIsDigital });
    setEditing("new");
  };
  const startEdit = (p) => {
    setForm({
      title: p.title, description: p.description, price: p.price, category: p.category,
      image_url: p.image_url, gallery: p.gallery || [], is_digital: p.is_digital, download_url: p.download_url || "",
      badge: p.badge || "", stock: p.stock,
      variants: Array.isArray(p.variants) ? p.variants.map((v) => ({ size: v.size, stock: v.stock || 0 })) : [],
      colors: Array.isArray(p.colors) ? p.colors.map((c) => ({ name: c.name, image_url: c.image_url })) : [],
      meta_title: p.meta_title || "",
      meta_description: p.meta_description || "",
      sections: Array.isArray(p.sections) ? [...p.sections] : [],
    });
    setEditing(p);
  };
  const close = () => { setEditing(null); setForm(empty); };

  const handleImage = async (e, field = "image_url") => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;
    const isDigital = field === "download_url";
    try {
      const file = isDigital ? rawFile : await maybeResizeImage(rawFile);
      const maxBytes = isDigital ? 200 * 1024 * 1024 : 20 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error(isDigital ? "Digital file must be under 200MB" : "Image too large — try a smaller one");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = isDigital ? "/upload/file" : "/upload/image";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success(isDigital ? "Digital file uploaded — customers will get this link in their receipt!" : "Image uploaded!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };
  const handleDigitalFile = (e) => handleImage(e, "download_url");

  const handleGalleryImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentCount = (form.gallery || []).length;
    const remaining = MAX_GALLERY - currentCount;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_GALLERY} extra photos reached`);
      e.target.value = "";
      return;
    }

    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only added the first ${remaining} — gallery max is ${MAX_GALLERY}`);
    }

    const uploadingId = toast.loading(`Uploading ${toUpload.length} photo${toUpload.length > 1 ? "s" : ""}…`);
    let okCount = 0;
    let failCount = 0;

    // Upload in parallel for speed
    const results = await Promise.all(toUpload.map(async (rawFile) => {
      try {
        const file = await maybeResizeImage(rawFile);
        if (file.size > 20 * 1024 * 1024) {
          throw new Error(`${rawFile.name} is over 20MB`);
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return { ok: true, url: data.url };
      } catch (err) {
        return { ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload failed" };
      }
    }));

    const newUrls = [];
    for (const r of results) {
      if (r.ok) { newUrls.push(r.url); okCount++; }
      else { failCount++; }
    }
    if (newUrls.length > 0) {
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...newUrls] }));
    }
    toast.dismiss(uploadingId);
    if (okCount > 0 && failCount === 0) toast.success(`Added ${okCount} photo${okCount > 1 ? "s" : ""} to gallery!`);
    else if (okCount > 0 && failCount > 0) toast.warning(`Added ${okCount}, ${failCount} failed`);
    else toast.error("All uploads failed");

    e.target.value = "";
  };

  const removeGallery = (idx) => {
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }));
  };

  // Variant helpers
  const addVariant = (sizeName) => {
    if (!sizeName) return;
    setForm((f) => {
      if ((f.variants || []).some((v) => v.size === sizeName)) return f;
      return { ...f, variants: [...(f.variants || []), { size: sizeName, stock: 0 }] };
    });
  };

  const addPreset = (sizes) => {
    setForm((f) => {
      const existing = new Set((f.variants || []).map((v) => v.size));
      const additions = sizes.filter((s) => !existing.has(s)).map((size) => ({ size, stock: 0 }));
      return { ...f, variants: [...(f.variants || []), ...additions] };
    });
  };

  const updateVariantStock = (idx, value) => {
    setForm((f) => {
      const next = [...(f.variants || [])];
      next[idx] = { ...next[idx], stock: value === "" ? 0 : Math.max(0, parseInt(value, 10) || 0) };
      return { ...f, variants: next };
    });
  };

  const removeVariant = (idx) => {
    setForm((f) => ({ ...f, variants: (f.variants || []).filter((_, i) => i !== idx) }));
  };

  // Color helpers
  const addColorRow = () => {
    setForm((f) => ({ ...f, colors: [...(f.colors || []), { name: "", image_url: "" }] }));
  };
  const updateColorName = (idx, name) => {
    setForm((f) => {
      const next = [...(f.colors || [])];
      next[idx] = { ...next[idx], name };
      return { ...f, colors: next };
    });
  };
  const removeColor = (idx) => {
    setForm((f) => ({ ...f, colors: (f.colors || []).filter((_, i) => i !== idx) }));
  };
  const uploadColorImage = async (idx, e) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) { return; }
    try {
      const file = await maybeResizeImage(rawFile);
      if (file.size > 20 * 1024 * 1024) { toast.error("Image too large"); e.target.value = ""; return; }
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => {
        const next = [...(f.colors || [])];
        next[idx] = { ...next[idx], image_url: data.url };
        return { ...f, colors: next };
      });
      toast.success("Color image uploaded!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const promoteToMain = (idx) => {
    setForm((f) => {
      const newGallery = [...f.gallery];
      const [picked] = newGallery.splice(idx, 1);
      const oldMain = f.image_url;
      return { ...f, image_url: picked, gallery: oldMain ? [oldMain, ...newGallery] : newGallery };
    });
    toast.success("Main photo updated!");
  };

  // Drag to reorder
  const onDragStart = (idx) => (e) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    setForm((f) => {
      const next = [...f.gallery];
      const [picked] = next.splice(from, 1);
      next.splice(idx, 0, picked);
      return { ...f, gallery: next };
    });
    dragIndex.current = null;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock), badge: form.badge || null, download_url: form.download_url || null, gallery: form.gallery || [], variants: (form.variants || []).map((v) => ({ size: v.size, stock: Number(v.stock) || 0 })), colors: (form.colors || []).filter((c) => c.name && c.image_url).map((c) => ({ name: c.name.trim(), image_url: c.image_url })), meta_title: form.meta_title || null, meta_description: form.meta_description || null };
      if (editing === "new") { await api.post("/products", payload); toast.success("Product created!"); }
      else { await api.patch(`/products/${editing.id}`, payload); toast.success("Product updated!"); }
      await fetchProducts();
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); await fetchProducts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  // Inline "Move to category" — one-click fix for miscategorized products
  const quickMove = async (p, newSlug) => {
    if (!newSlug || newSlug === p.category) return;
    const newCat = categories.find((c) => c.slug === newSlug);
    if (!newCat) return;
    // Optimistic update so the card jumps categories instantly
    setProducts((list) => list.map((x) => x.id === p.id ? { ...x, category: newSlug } : x));
    try {
      await api.patch(`/products/${p.id}`, { category: newSlug });
      toast.success(`Moved "${p.title}" to ${newCat.name}`);
    } catch (err) {
      // Revert on failure
      setProducts((list) => list.map((x) => x.id === p.id ? { ...x, category: p.category } : x));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
    }
  };

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(new Set(filteredProducts.map((p) => p.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); setBulkCategory(""); };

  const bulkMove = async () => {
    if (!bulkCategory) { toast.error("Pick a category first"); return; }
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const targetCat = categories.find((c) => c.slug === bulkCategory);
    if (!targetCat) return;
    // Optimistic
    const prevSnapshot = products;
    setProducts((list) => list.map((p) => selectedIds.has(p.id) ? { ...p, category: bulkCategory } : p));
    try {
      const { data } = await api.post("/products/bulk-move", { ids, category: bulkCategory });
      toast.success(`Moved ${data.modified} product${data.modified === 1 ? "" : "s"} to ${targetCat.name}`);
      exitSelectMode();
    } catch (err) {
      setProducts(prevSnapshot);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk move failed");
    }
  };

  // Featured-order drag-and-drop (only within a specific category filter)
  const canReorder = reorderMode && categoryFilter !== "all" && categoryFilter !== "__orphans__";
  const onReorderDragStart = (id) => (e) => {
    reorderDragId.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onReorderDragOver = (e) => {
    if (!canReorder) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onReorderDrop = (targetId) => async (e) => {
    e.preventDefault();
    const fromId = reorderDragId.current;
    reorderDragId.current = null;
    if (!fromId || fromId === targetId || !canReorder) return;
    // Build the new ordered list inside this category
    const sectionIds = filteredProducts.map((p) => p.id);
    const fromIdx = sectionIds.indexOf(fromId);
    const toIdx = sectionIds.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const newOrder = [...sectionIds];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, fromId);
    // Optimistic: reorder products list (only the slice we control)
    const prevSnapshot = products;
    const orderMap = new Map(newOrder.map((id, idx) => [id, idx]));
    setProducts((list) => {
      const inCat = list.filter((p) => sectionIds.includes(p.id))
        .map((p) => ({ ...p, position: orderMap.get(p.id) }))
        .sort((a, b) => a.position - b.position);
      const outCat = list.filter((p) => !sectionIds.includes(p.id));
      return [...inCat, ...outCat];
    });
    try {
      await api.post("/products/reorder", { ids: newOrder });
      toast.success("Order updated");
    } catch (err) {
      setProducts(prevSnapshot);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reorder failed");
    }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 py-12">
      <PreviewModeBanner />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-script text-3xl text-[#2eb6a8]">Control Room</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1c1815] tracking-tight">Admin Dashboard</h1>
          <p className="text-[#4a4239] text-sm mt-1">Manage your products and customize your homepage layout.</p>
        </div>
        {tab === "products" && (
          <div className="flex items-center gap-2">
            {categoryFilter !== "all" && categoryFilter !== "__orphans__" && !selectMode && (
              <button
                type="button"
                onClick={() => setReorderMode((v) => !v)}
                data-testid="admin-reorder-toggle"
                title="Drag products to set their order on the shop page"
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] px-4 py-3 rounded-full border transition-all ${
                  reorderMode
                    ? "bg-[#9c6dde] text-white border-[#9c6dde]"
                    : "bg-white text-[#4a4239] border-[#e5dccd] hover:border-[#9c6dde]"
                }`}
              >
                <GripVertical size={13}/> {reorderMode ? "Done" : "Reorder"}
              </button>
            )}
            {!reorderMode && (
              <button
                type="button"
                onClick={() => { if (selectMode) exitSelectMode(); else setSelectMode(true); }}
                data-testid="admin-select-toggle"
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] px-4 py-3 rounded-full border transition-all ${
                  selectMode
                    ? "bg-[#1c1815] text-white border-[#1c1815]"
                    : "bg-white text-[#4a4239] border-[#e5dccd] hover:border-[#1c1815]"
                }`}
              >
                {selectMode ? <><X size={13}/> Cancel</> : <><CheckSquare size={13}/> Select</>}
              </button>
            )}
            <button onClick={startNew} data-testid="admin-new-product" className="inline-flex items-center gap-2 bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 rounded-full transition-all">
              <Plus size={16}/> {selectedCat ? `New in ${selectedCat.name}` : "New Product"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-tabs">
        <button onClick={() => setTab("products")} data-testid="tab-products"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full transition-all ${
            tab === "products" ? "bg-[#2eb6a8] text-white shadow-md" : "bg-white border border-[#e5dccd] text-[#4a4239] hover:border-[#2eb6a8] hover:text-[#1c1815]"
          }`}>
          <Package size={13}/> Products
        </button>
        <button onClick={() => setTab("orders")} data-testid="tab-orders"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full transition-all ${
            tab === "orders" ? "bg-[#9c6dde] text-white shadow-md" : "bg-white border border-[#e5dccd] text-[#4a4239] hover:border-[#9c6dde] hover:text-[#1c1815]"
          }`}>
          <Mail size={13}/> Orders
        </button>
        <button onClick={() => setTab("categories")} data-testid="tab-categories"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full transition-all ${
            tab === "categories" ? "bg-[#e8478e] text-white shadow-md" : "bg-white border border-[#e5dccd] text-[#4a4239] hover:border-[#e8478e] hover:text-[#1c1815]"
          }`}>
          <Tag size={13}/> Categories
        </button>
        <button onClick={() => setTab("sections")} data-testid="tab-sections"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full transition-all ${
            tab === "sections" ? "bg-[#9c6dde] text-white shadow-md" : "bg-white border border-[#e5dccd] text-[#4a4239] hover:border-[#9c6dde] hover:text-[#1c1815]"
          }`}>
          <FolderInput size={13}/> Sections
        </button>
        <button onClick={() => setTab("homepage")} data-testid="tab-homepage"
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full transition-all ${
            tab === "homepage" ? "bg-[#c93875] text-white shadow-md" : "bg-white border border-[#e5dccd] text-[#4a4239] hover:border-[#c93875] hover:text-[#1c1815]"
          }`}>
          <LayoutGrid size={13}/> Homepage
        </button>
      </div>

      {tab === "homepage" && (
        <div className="mt-8">
          <HomeSectionsManager/>
        </div>
      )}

      {tab === "sections" && (
        <div className="mt-8">
          <SectionsManager onChange={fetchSections}/>
        </div>
      )}

      {tab === "categories" && (
        <div className="mt-8">
          <CategoriesManager onChange={fetchCategories}/>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8">
          <AdminOrders/>
        </div>
      )}

      {tab === "products" && (<>
      <LowStockBanner/>
      <RestockWaitlist/>
      <LiveCartsWidget/>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Total" value={products.length} color="text-[#2eb6a8]"/>
        <Stat label="Digital" value={products.filter((p) => p.is_digital).length} color="text-[#9c6dde]"/>
        <Stat label="Best Sellers" value={products.filter((p) => p.badge === "bestseller").length} color="text-[#e8478e]"/>
        <Stat label="Sized Items" value={products.filter((p) => Array.isArray(p.variants) && p.variants.length > 0).length} color="text-[#c93875]"/>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-category-filter">
        <button
          type="button"
          onClick={() => setCategoryFilter("all")}
          data-testid="cat-filter-all"
          className={`text-[11px] font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-all ${
            categoryFilter === "all"
              ? "bg-[#1c1815] text-white border-[#1c1815]"
              : "bg-white text-[#4a4239] border-[#e5dccd] hover:border-[#1c1815]"
          }`}
        >
          All · {products.length}
        </button>
        {categories.map((c) => {
          const count = products.filter((p) => p.category === c.slug).length;
          const active = categoryFilter === c.slug;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategoryFilter(c.slug)}
              data-testid={`cat-filter-${c.slug}`}
              style={active ? { backgroundColor: c.accent_color, borderColor: c.accent_color } : { borderColor: "#e5dccd" }}
              className={`text-[11px] font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-all ${
                active ? "text-white" : "bg-white text-[#4a4239] hover:text-[#1c1815]"
              }`}
            >
              {c.name} · {count}
            </button>
          );
        })}
        {(() => {
          const validSlugs = new Set(categories.map((c) => c.slug));
          const orphans = products.filter((p) => !validSlugs.has(p.category));
          if (orphans.length === 0) return null;
          const active = categoryFilter === "__orphans__";
          return (
            <button
              type="button"
              onClick={() => setCategoryFilter("__orphans__")}
              data-testid="cat-filter-orphans"
              title="Products whose category no longer exists or wasn't set — use the dropdown on each card to move them"
              className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-all ${
                active
                  ? "bg-[#c93875] text-white border-[#c93875]"
                  : "bg-[#c93875]/10 text-[#c93875] border-[#c93875]/40 hover:bg-[#c93875]/15"
              }`}
            >
              <AlertTriangle size={11}/> Needs Category · {orphans.length}
            </button>
          );
        })()}
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-product-grid">
        {canReorder && (
          <div className="col-span-full flex items-center gap-2 text-[11px] text-[#9c6dde] bg-[#9c6dde]/10 border border-[#9c6dde]/30 rounded-xl px-3 py-2" data-testid="reorder-hint">
            <GripVertical size={12}/>
            <span className="font-bold uppercase tracking-[0.16em]">Drag cards</span>
            <span>to set the order shoppers see on /shop and homepage category sections.</span>
          </div>
        )}
        {filteredProducts.map((p) => {
          const isOut = !p.is_digital && p.stock <= 0;
          const currentCat = categories.find((c) => c.slug === p.category);
          const isOrphan = !currentCat && p.category !== "uncategorized";
          const isSelected = selectedIds.has(p.id);
          return (
            <div
              key={p.id}
              data-testid={`product-card-${p.id}`}
              draggable={canReorder}
              onDragStart={canReorder ? onReorderDragStart(p.id) : undefined}
              onDragOver={canReorder ? onReorderDragOver : undefined}
              onDrop={canReorder ? onReorderDrop(p.id) : undefined}
              onClick={() => { if (canReorder) return; if (selectMode) toggleSelect(p.id); else startEdit(p); }}
              className={`group flex items-center gap-3 p-3 rounded-2xl border bg-white hover:shadow-md transition-all ${
                canReorder
                  ? "cursor-grab active:cursor-grabbing border-[#9c6dde]/40 hover:border-[#9c6dde]"
                  : "cursor-pointer"
              } ${
                isSelected
                  ? "border-[#2eb6a8] bg-[#2eb6a8]/8 ring-2 ring-[#2eb6a8]/40"
                  : isOut ? "border-[#c93875]/60 bg-[#c93875]/5"
                  : isOrphan ? "border-[#c93875] bg-[#c93875]/8"
                  : !canReorder ? "border-[#e5dccd] hover:border-[#2eb6a8]"
                  : ""
              }`}
            >
              {canReorder && (
                <div data-testid={`reorder-handle-${p.id}`} className="flex-shrink-0 text-[#9c6dde]">
                  <GripVertical size={18}/>
                </div>
              )}
              {selectMode && !canReorder && (
                <div data-testid={`select-${p.id}`} className="flex-shrink-0">
                  {isSelected
                    ? <CheckSquare size={20} className="text-[#2eb6a8]"/>
                    : <Square size={20} className="text-[#8a8076]"/>}
                </div>
              )}
              <img src={p.image_url} alt="" className="w-16 h-16 object-cover rounded-xl flex-shrink-0 pointer-events-none"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1c1815] truncate leading-tight" title={p.title}>{p.title}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] flex-wrap">
                  <span className="font-bold text-[#1c1815]">${p.price.toFixed(2)}</span>
                  <span className="text-[#8a8076]">·</span>
                  {p.is_digital ? (
                    <span className="text-[#9c6dde] uppercase tracking-[0.18em] font-bold">∞ Digital</span>
                  ) : (
                    <span data-testid={`stock-${p.id}`} className={`uppercase tracking-[0.18em] font-bold ${isOut ? "text-[#c93875]" : "text-[#1c1815]"}`}>
                      {isOut ? "Sold Out" : `${p.stock} in stock`}
                    </span>
                  )}
                  {p.badge && (
                    <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold tracking-[0.18em] ${p.badge === "bestseller" ? "bg-[#e8478e]/12 text-[#c93875]" : "bg-[#2eb6a8]/12 text-[#1f8d83]"}`}>
                      {p.badge}
                    </span>
                  )}
                  {cartCounts[p.id] && cartCounts[p.id].qty > 0 && (
                    <span
                      data-testid={`cart-pill-${p.id}`}
                      title={`${cartCounts[p.id].qty} in ${cartCounts[p.id].shoppers} shopper cart${cartCounts[p.id].shoppers === 1 ? "" : "s"}`}
                      className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full uppercase font-bold tracking-[0.18em] bg-[#2eb6a8]/15 text-[#1f8d83]"
                    >
                      <ShoppingCart size={9}/> {cartCounts[p.id].qty} in cart{cartCounts[p.id].qty === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                {/* Inline category move — fixes miscategorized products in one click */}
                {!selectMode && !canReorder && (
                  <div className="mt-1.5 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} data-testid={`move-wrap-${p.id}`}>
                    <FolderInput size={11} className={isOrphan ? "text-[#c93875]" : "text-[#8a8076]"}/>
                    <select
                      value={currentCat ? p.category : ""}
                      onChange={(e) => quickMove(p, e.target.value)}
                      data-testid={`move-${p.id}`}
                      title="Move to category"
                      style={currentCat ? { color: currentCat.accent_color, borderColor: currentCat.accent_color + "55" } : {}}
                      className={`text-[10px] font-bold uppercase tracking-[0.14em] bg-white border rounded-full px-2 py-1 outline-none focus:ring-2 focus:ring-[#2eb6a8]/30 cursor-pointer hover:border-[#2eb6a8] transition-colors ${isOrphan ? "border-[#c93875] text-[#c93875]" : "border-[#e5dccd] text-[#4a4239]"}`}
                    >
                      {!currentCat && (
                        <option value="" disabled>{isOrphan ? `! ${p.category}` : "Uncategorized"}</option>
                      )}
                      {categories.map((c) => (
                        <option key={c.id} value={c.slug}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {!selectMode && !canReorder && (
                <div className="flex flex-col gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => startEdit(p)} data-testid={`edit-${p.id}`}
                    title="Edit"
                    className="p-2 hover:bg-[#2eb6a8]/12 rounded-lg text-[#2eb6a8] transition-colors">
                    <Pencil size={15}/>
                  </button>
                  <button onClick={() => del(p)} data-testid={`delete-${p.id}`}
                    title="Delete"
                    className="p-2 hover:bg-[#e8478e]/12 rounded-lg text-[#e8478e] transition-colors">
                    <Trash2 size={15}/>
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {filteredProducts.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#8a8076] rounded-2xl border border-dashed border-[#e5dccd]">
            {categoryFilter === "all"
              ? "No products yet."
              : categoryFilter === "__orphans__"
                ? "Nothing to fix — every product is in a real category."
                : `No products in ${selectedCat?.name || categoryFilter} yet. Click "New in ${selectedCat?.name || categoryFilter}" above to add one.`}
          </div>
        )}
      </div>

      {/* Floating bulk-action bar — only visible while selecting */}
      {selectMode && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[min(95%,720px)] bg-[#1c1815] text-white rounded-full shadow-2xl border border-[#1c1815] flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5"
        >
          <span data-testid="bulk-count" className="text-[11px] font-bold uppercase tracking-[0.18em] pl-2 whitespace-nowrap">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={selectAllVisible}
            data-testid="bulk-select-all"
            className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#bbb1a3] hover:text-white px-2 py-1.5 rounded-full transition-colors whitespace-nowrap"
            disabled={filteredProducts.length === 0}
          >
            Select all{categoryFilter !== "all" ? " visible" : ""}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#bbb1a3] hover:text-white px-2 py-1.5 rounded-full transition-colors"
            disabled={selectedIds.size === 0}
          >
            Clear
          </button>
          <div className="hidden sm:block flex-1"/>
          <select
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            data-testid="bulk-category-select"
            className="bg-white text-[#1c1815] text-[11px] font-bold uppercase tracking-[0.16em] rounded-full px-3 py-2 outline-none focus:ring-2 focus:ring-[#2eb6a8]/40 cursor-pointer min-w-0"
            disabled={selectedIds.size === 0}
          >
            <option value="">Move to…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={bulkMove}
            data-testid="bulk-move-submit"
            className="bg-[#2eb6a8] hover:bg-[#1f8d83] text-white text-[11px] font-bold uppercase tracking-[0.16em] px-4 py-2 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            disabled={selectedIds.size === 0 || !bulkCategory}
          >
            Move
          </button>
        </div>
      )}
      </>)}

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:p-8 overflow-y-auto bg-[#1c1815]/45 backdrop-blur-sm" onClick={close}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white border border-[#e5dccd] rounded-3xl w-full max-w-2xl p-7 sm:p-9 my-8 shadow-2xl" data-testid="admin-form">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-extrabold text-2xl text-[#1c1815]">{editing === "new" ? "New Product" : "Edit Product"}</h2>
              <button type="button" onClick={close} className="p-2 hover:bg-[#efe5d4] rounded-full"><X size={20}/></button>
            </div>
            {(() => {
              const formCat = categories.find((c) => c.slug === form.category);
              const isOrphan = !formCat && form.category && form.category !== "uncategorized";
              return (
                <div
                  data-testid="form-category-banner"
                  className={`mb-5 flex items-center justify-between gap-3 rounded-xl px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] ${
                    isOrphan
                      ? "bg-[#c93875]/12 text-[#c93875] border border-[#c93875]/40"
                      : "bg-[#faf4e9] text-[#4a4239] border border-[#e5dccd]"
                  }`}
                  style={formCat ? { borderColor: formCat.accent_color + "55", color: formCat.accent_color } : {}}
                >
                  <span className="flex items-center gap-2">
                    <Tag size={12}/>
                    Saving to: {formCat ? formCat.name : (form.category || "— pick a category below —")}
                    {isOrphan && <span className="normal-case tracking-normal text-[10px] font-normal">(category doesn't exist — fix below)</span>}
                  </span>
                </div>
              );
            })()}

            <div className="space-y-4">
              <Field label="Title" testid="form-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required/>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Description</label>
                <textarea rows={4} required data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none resize-y text-[#1c1815]"/>
              </div>

              <details
                className="bg-[#faf4e9] border border-[#e5dccd] rounded-xl px-4 py-3"
                data-testid="seo-section"
                open={!!(form.meta_title || form.meta_description)}
              >
                <summary className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold cursor-pointer select-none">SEO &amp; Sharing (optional)</summary>
                <p className="text-[11px] text-[#8a8076] mt-2">Custom title &amp; description for Google search results and link previews. Leave blank to use the product title/description.</p>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Meta Title <span className="text-[#bbb1a3] normal-case">({(form.meta_title || "").length}/60)</span></label>
                    <input type="text" maxLength={70} data-testid="form-meta-title"
                      value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                      placeholder={`${form.title || "Product"} · Snip Happens Boutique`}
                      className="mt-1.5 w-full bg-white border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-3 py-2 text-sm outline-none text-[#1c1815]"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Meta Description <span className="text-[#bbb1a3] normal-case">({(form.meta_description || "").length}/160)</span></label>
                    <textarea rows={2} maxLength={170} data-testid="form-meta-description"
                      value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                      placeholder="A short snippet (~155 chars) that shows up under the title in Google results."
                      className="mt-1.5 w-full bg-white border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-3 py-2 text-sm outline-none resize-y text-[#1c1815]"/>
                  </div>
                </div>
              </details>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Price (USD)" testid="form-price" type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required/>
                <Field label={form.variants && form.variants.length > 0 ? "Stock (unused — sized below)" : "Stock"} testid="form-stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })}/>
              </div>

              {!form.is_digital && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Sizes &amp; Stock</label>
                    <div className="flex flex-wrap gap-1.5">
                      <button type="button" data-testid="preset-adult-regular" onClick={() => addPreset(ADULT_REGULAR)}
                        className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-2.5 py-1.5 rounded-full">+ Adult XS-XL</button>
                      <button type="button" data-testid="preset-adult-plus" onClick={() => addPreset(ADULT_PLUS)}
                        className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-2.5 py-1.5 rounded-full">+ Plus 1X-4X</button>
                      <button type="button" data-testid="preset-youth" onClick={() => addPreset(YOUTH)}
                        className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-2.5 py-1.5 rounded-full">+ Youth</button>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#8a8076] mt-1">Add the sizes you carry and how many of each are in stock.</p>
                  {(form.variants || []).length === 0 ? (
                    <div className="mt-2 p-4 rounded-xl border border-dashed border-[#e5dccd] text-center text-[#8a8076] text-xs">
                      No sizes yet. Pick a preset or add one below.
                    </div>
                  ) : (
                    <div className="mt-2 space-y-1.5" data-testid="variants-list">
                      {form.variants.map((v, idx) => (
                        <div key={`${v.size}-${idx}`} className="flex items-center gap-2 bg-[#faf4e9] border border-[#e5dccd] rounded-xl pl-3 pr-1.5 py-1.5" data-testid={`variant-row-${v.size}`}>
                          <div className="font-bold text-[#1c1815] w-20 text-sm">{v.size}</div>
                          <input type="number" min="0" value={v.stock}
                            onChange={(e) => updateVariantStock(idx, e.target.value)}
                            data-testid={`variant-stock-${v.size}`}
                            className="flex-1 bg-white border border-[#e5dccd] focus:border-[#2eb6a8] rounded-lg px-3 py-1.5 text-sm outline-none text-[#1c1815]"
                            placeholder="Stock"/>
                          <button type="button" onClick={() => removeVariant(idx)} title="Remove size"
                            data-testid={`variant-remove-${v.size}`}
                            className="text-[#8a8076] hover:text-[#c93875] hover:bg-white p-1.5 rounded-md transition-colors">
                            <X size={14}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <select onChange={(e) => { if (e.target.value) addVariant(e.target.value); e.target.value = ""; }} value=""
                    data-testid="variant-add-select"
                    className="mt-2 bg-white border border-[#e5dccd] rounded-xl px-3 py-2 text-sm text-[#1c1815] focus:border-[#2eb6a8] outline-none">
                    <option value="">+ Add a size…</option>
                    {ALL_SIZES.filter((s) => !form.variants.some((v) => v.size === s)).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}

              {!form.is_digital && (
                <div data-testid="colors-block">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Color Options (optional)</label>
                    <button type="button" data-testid="color-add" onClick={addColorRow}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#e8478e] hover:bg-[#c93875] text-white px-3 py-1.5 rounded-full">
                      + Add Color
                    </button>
                  </div>
                  <p className="text-[11px] text-[#8a8076] mt-1">Add a name + image for each color. Customers will see a dropdown and the photo will swap when they pick one.</p>
                  {(form.colors || []).length === 0 ? (
                    <div className="mt-2 p-4 rounded-xl border border-dashed border-[#e5dccd] text-center text-[#8a8076] text-xs">
                      No color options yet. Click "Add Color" to start.
                    </div>
                  ) : (
                    <div className="mt-2 space-y-2" data-testid="colors-list">
                      {form.colors.map((c, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-[#faf4e9] border border-[#e5dccd] rounded-xl p-2" data-testid={`color-row-${idx}`}>
                          {c.image_url ? (
                            <img src={c.image_url} alt={c.name} className="w-14 h-14 rounded-lg object-cover border border-[#e5dccd] flex-shrink-0"/>
                          ) : (
                            <div className="w-14 h-14 rounded-lg border-2 border-dashed border-[#e5dccd] flex items-center justify-center text-[10px] text-[#8a8076] flex-shrink-0">No img</div>
                          )}
                          <input
                            type="text"
                            value={c.name}
                            onChange={(e) => updateColorName(idx, e.target.value)}
                            placeholder="Color name (e.g. Black, Hot Pink)"
                            data-testid={`color-name-${idx}`}
                            className="flex-1 bg-white border border-[#e5dccd] focus:border-[#2eb6a8] rounded-lg px-3 py-2 text-sm outline-none text-[#1c1815]"
                          />
                          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-bold uppercase tracking-[0.18em] bg-white hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-3 py-2 rounded-lg" data-testid={`color-upload-label-${idx}`}>
                            <Upload size={12}/> {c.image_url ? "Change" : "Upload"}
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadColorImage(idx, e)} data-testid={`color-upload-${idx}`}/>
                          </label>
                          <button type="button" onClick={() => removeColor(idx)} title="Remove color"
                            data-testid={`color-remove-${idx}`}
                            className="text-[#8a8076] hover:text-[#c93875] p-2 rounded-md transition-colors">
                            <X size={14}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Category</label>
                  <select value={form.category} data-testid="form-category" onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] rounded-xl px-4 py-3 focus:border-[#2eb6a8] outline-none text-[#1c1815]">
                    {categories.length === 0 && (
                      <option value="">— Add categories first —</option>
                    )}
                    {categories.map((c) => (
                      <option key={c.id} value={c.slug}>{c.name}</option>
                    ))}
                    {form.category === "uncategorized" && (
                      <option value="uncategorized">Uncategorized</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Badge</label>
                  <select value={form.badge} data-testid="form-badge" onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] rounded-xl px-4 py-3 focus:border-[#2eb6a8] outline-none text-[#1c1815]">
                    <option value="">None</option>
                    <option value="bestseller">Best Seller</option>
                    <option value="trending">Trending</option>
                  </select>
                </div>
              </div>

              {/* Sections multi-select (filtered by selected category) */}
              {(() => {
                const available = sections.filter((s) => s.category_slug === form.category);
                if (available.length === 0) return null;
                const toggle = (slug) => {
                  setForm((f) => {
                    const cur = Array.isArray(f.sections) ? f.sections : [];
                    return {
                      ...f,
                      sections: cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug],
                    };
                  });
                };
                const selected = new Set(form.sections || []);
                return (
                  <div data-testid="form-sections-wrap">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">
                      Sections <span className="text-[#9c6dde] normal-case tracking-normal">· pick all that apply</span>
                    </label>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {available.map((s) => {
                        const active = selected.has(s.slug);
                        return (
                          <button
                            type="button"
                            key={s.id}
                            onClick={() => toggle(s.slug)}
                            data-testid={`form-section-${s.slug}`}
                            style={active ? { backgroundColor: s.accent_color, borderColor: s.accent_color } : { borderColor: "#e5dccd" }}
                            className={`text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border transition-all ${
                              active ? "text-white" : "bg-white text-[#4a4239] hover:text-[#1c1815]"
                            }`}
                          >
                            {active && "✓ "}{s.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div className="flex items-center gap-3">
                <input type="checkbox" id="is_digital" data-testid="form-is-digital" checked={form.is_digital} onChange={(e) => setForm({ ...form, is_digital: e.target.checked })}
                  className="w-4 h-4 accent-[#2eb6a8]"/>
                <label htmlFor="is_digital" className="text-sm text-[#1c1815]">This is a digital product (instant download)</label>
              </div>

              {form.is_digital && (
                <div>
                  <Field label="Download URL (file link)" testid="form-download-url" value={form.download_url} onChange={(v) => setForm({ ...form, download_url: v })} placeholder="https://… or upload below"/>
                  <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm bg-[#faf4e9] hover:bg-[#efe5d4] px-4 py-2.5 rounded-xl border border-[#e5dccd] text-[#1c1815]">
                    <Upload size={14}/> Upload digital file (ZIP, PDF, PNG… up to 200MB)
                    <input type="file" className="hidden" data-testid="form-digital-file" onChange={handleDigitalFile}/>
                  </label>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Main Product Image</label>
                  <span className="text-[10px] text-[#8a8076]">Shown on cards & as cover</span>
                </div>
                <div className="mt-1.5 flex gap-3 items-start">
                  {form.image_url && <img src={form.image_url} alt="" className="w-24 h-24 object-cover rounded-xl border border-[#e5dccd]"/>}
                  <div className="flex-1 space-y-2">
                    <input type="text" placeholder="Paste image URL" data-testid="form-image-url"
                      value={form.image_url.startsWith("data:") ? "" : form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      className="w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-4 py-2.5 text-sm outline-none text-[#1c1815]"/>
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm bg-[#faf4e9] hover:bg-[#efe5d4] px-4 py-2.5 rounded-xl border border-[#e5dccd] text-[#1c1815]">
                      <Upload size={14}/> Or upload from device
                      <input type="file" accept="image/*" className="hidden" data-testid="form-image-file" onChange={handleImage}/>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Gallery Photos (mockups & extras)</label>
                  <span className="text-[10px] text-[#8a8076]" data-testid="gallery-count">{(form.gallery || []).length} / {MAX_GALLERY}</span>
                </div>
                <p className="text-[11px] text-[#8a8076] mt-1">Up to {MAX_GALLERY} more photos. Drag to reorder. Customers swipe through them on the product page.</p>

                {(form.gallery || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-5 gap-2" data-testid="gallery-grid">
                    {form.gallery.map((url, idx) => (
                      <div
                        key={url + idx}
                        draggable
                        onDragStart={onDragStart(idx)}
                        onDragOver={onDragOver}
                        onDrop={onDrop(idx)}
                        data-testid={`gallery-item-${idx}`}
                        className="relative group aspect-square cursor-grab active:cursor-grabbing"
                      >
                        <img src={url} alt="" className="w-full h-full object-cover rounded-xl border border-[#e5dccd] pointer-events-none"/>
                        <div className="absolute inset-0 bg-[#1c1815]/0 group-hover:bg-[#1c1815]/15 transition-colors rounded-xl pointer-events-none"/>
                        <div className="absolute top-1 left-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Drag to reorder">
                          <GripVertical size={11} className="text-[#1c1815]"/>
                        </div>
                        <button type="button" onClick={() => promoteToMain(idx)} title="Make this the main photo"
                          className="absolute bottom-1 left-1 bg-[#2eb6a8] text-white text-[9px] font-bold uppercase tracking-[0.1em] px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#1f8d83]"
                          data-testid={`gallery-promote-${idx}`}>
                          Cover
                        </button>
                        <button type="button" onClick={() => removeGallery(idx)} title="Remove"
                          className="absolute top-1 right-1 bg-[#c93875] hover:bg-[#e8478e] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                          data-testid={`gallery-remove-${idx}`}>
                          <X size={12}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {(form.gallery || []).length < MAX_GALLERY && (
                  <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm bg-[#faf4e9] hover:bg-[#efe5d4] px-4 py-2.5 rounded-xl border border-[#e5dccd] text-[#1c1815]" data-testid="gallery-upload-label">
                    <Upload size={14}/> Add gallery photos
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="form-gallery-file" onChange={handleGalleryImage}/>
                  </label>
                )}
              </div>

              <button type="submit" disabled={saving} data-testid="form-submit" className="w-full bg-[#2eb6a8] hover:bg-[#1f8d83] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs py-3.5 rounded-full transition-all disabled:opacity-60">
                {saving ? "Saving…" : (editing === "new" ? "Create Product" : "Save Changes")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, placeholder, step }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">{label}</label>
      <input type={type} step={step} required={required} placeholder={placeholder} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
    </div>
  );
}
function Stat({ label, value, color, alert }) {
  return (
    <div className={`p-4 rounded-2xl border ${alert ? "border-[#e8478e]/50 bg-[#e8478e]/8" : "border-[#e5dccd] bg-white"} shadow-sm`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">{label}</div>
      <div className={`font-display font-extrabold text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}


function PreviewModeBanner() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  // Only show on preview / dev URLs — not on the live custom domain
  const isPreview = host.includes("emergentagent.com") || host.includes("localhost") || host.includes("127.0.0.1");
  if (!isPreview) return null;
  return (
    <div
      data-testid="preview-mode-banner"
      className="mb-6 flex items-start gap-3 rounded-2xl border-2 border-[#e8478e] bg-[#e8478e]/10 px-5 py-4"
    >
      <AlertTriangle size={20} className="text-[#c93875] mt-0.5 flex-shrink-0"/>
      <div className="text-sm leading-relaxed">
        <div className="font-display font-extrabold text-[#1c1815] text-base mb-0.5">
          You're in the PREVIEW (test) site
        </div>
        <div className="text-[#4a4239]">
          Changes you make here do <strong>NOT</strong> affect your live store. To manage real products and orders, log into the admin at{" "}
          <a
            href="https://sniphappensboutique.com/admin"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#c93875] font-bold underline hover:text-[#e8478e]"
            data-testid="live-admin-link"
          >
            sniphappensboutique.com/admin
          </a>.
        </div>
      </div>
    </div>
  );
}
